import {
  $UserAddPermissions,
  ACTIONS,
  UserPriorityEnum,
} from "@store/user-store";
import {
  ArrowDropDownSVG,
  CheckboxCheckedSVG,
  CheckboxSVG,
  GreyArrowDownSVG,
} from "@components/svgs";
import { Checkbox, TableCell, TableRow, Tooltip } from "@mui/material";
import { FC, memo, useEffect } from "react";
import { useTableColumnsWidth } from "@hooks/useTableColumnsWidth";

import { ColorfulButton } from "@components/common/common";
import SpecialistPlanService from "@services/specialist-plan-service";
import { TrashIcon } from "@phosphor-icons/react";
import { WorkplanGroupItem } from "../workplan-group-item/workplan-group-item";
import { WrapperSVG } from "@components/WrapperSVG/WrapperSVG";
import clsx from "clsx";
import styles from "./workplan-group.module.scss";
import useModal from "@modals/modal-hook";
import { useSelectedCompanyQuery } from "@lib/company";
import { useUnit } from "effector-react";
import { GetWorkplanTasksFilters, WorkplanGroupT } from "@interfaces/workplan";
import { useWorkplanTasksQuery } from "@lib/workplan";
import { Button } from "@ui/button";
import { UseSortingResult } from "@hooks/useSorting";
import { useQueryClient } from "react-query";
import { workplanQueryKeys } from "@lib/workplan/queryKeys";
import { Loader } from "@mantine/core";
import Badge from "@mui/material/Badge";

type TWorkplanGroup = {
  isOpen: boolean;
  isSelected: boolean;
  group: WorkplanGroupT;
  filters: GetWorkplanTasksFilters;
  sorting: UseSortingResult;
  isCompanyDefined: boolean;
  categoryId?: number | null;
  hideEmptyOptions?: boolean;
  listCompanies?: { title: string; id: number }[];
  withCheckbox?: boolean;
  choice: any;
  onToggleOpen: () => void;
  onDeleteSelectedGroups: () => void;
  onSelectChange: (groupId: number, checked: boolean) => void;
};
export const WorkplanGroup: FC<TWorkplanGroup> = ({
  isOpen,
  isSelected,
  group,
  filters,
  sorting,
  isCompanyDefined,
  categoryId = null,
  hideEmptyOptions = false,
  withCheckbox = true,
  choice,
  onDeleteSelectedGroups,
  onToggleOpen,
  onSelectChange,
}) => {
  const table = document.querySelector("#resizable-table") as HTMLElement;
  const [columnsWidth, updateColumnsWidth] = useTableColumnsWidth(
    "workplan",
    [100, 100, 100, 100, 100]
  );

  const queryClient = useQueryClient();

  const { company } = useSelectedCompanyQuery();

  const { open } = useModal();
  const permissions = useUnit($UserAddPermissions);

  const { data, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage } =
    useWorkplanTasksQuery(
      {
        groupTitle: group.title,
        limit: 50,
        companies: filters.companies,
        statuses: filters.statuses,
        startDate: filters.startDate
          ? filters.startDate.toISOString()
          : undefined,
        endDate: filters.deadline ? filters.deadline.toISOString() : undefined,
        ...sorting.getActiveSorting(),
      },
      {
        enabled: isOpen,
      }
    );

  useEffect(() => {
    if (isOpen && data) {
      const elements = document.querySelectorAll(
        ".resizable"
      ) as any as HTMLCollectionOf<HTMLDivElement>;
      const resizers = document.querySelectorAll(".resizable .resizer");
      for (let i = 0; i < resizers.length; i++) {
        const currentResizer = resizers[i];

        const resize = (e: any) => {
          let resultWidth = Math.round(
            e.pageX - elements[i].getBoundingClientRect().left
          );
          if (resultWidth < 150) resultWidth = 150;
          if (resultWidth > 500) resultWidth = 500;
          if (isCompanyDefined) {
            columnsWidth[i + 1] = resultWidth;
          } else {
            columnsWidth[i] = resultWidth;
          }
          updateColumnsWidth([...columnsWidth]);

          table.style.width =
            columnsWidth.reduce((acc, value, index) => {
              if (isCompanyDefined && index === 0) return acc;
              return acc + value;
            }, 0) + "px";
        };

        const stopResize = () => {
          window.removeEventListener("mousemove", resize);
          window.removeEventListener("mouseup", stopResize);

          updateColumnsWidth(columnsWidth);
        };

        currentResizer.addEventListener("mousedown", function (e) {
          e.preventDefault();
          window.addEventListener("mousemove", resize);
          window.addEventListener("mouseup", stopResize);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, data]);

  const handleGroupDeleting = (e: any) => {
    e.stopPropagation();
    if (!company) return;

    if (permissions.hasRequiredLevel(UserPriorityEnum.Admin)) {
      open("ConfirmActionModal", {
        btnText: "Удалить",
        modalData: {
          text: `Вы уверены что хотите удалить категорию "${group.title}" и все её задачи?`,
        },
        onConfirm: () =>
          SpecialistPlanService.deleteGroup(
            company.id,
            group.id ?? -1,
            (err, res) => {
              if (err || !res) {
                if (err?.message) {
                  open("NotificationModal", {
                    btnText: "Хорошо",
                    modalData: {
                      modalTitle: "Ошибка при удалении",
                      titleStyle: {
                        color: "#b00420",
                      },
                      text: err?.message,
                      btnText: "Хорошо",
                    },
                  });
                }
                return console.error("При удалении категории произошла ошибка");
              }
              if (!!res.data) {
                queryClient.invalidateQueries(workplanQueryKeys.all);
              }
            }
          ),
      });
    } else {
      open("NotificationModal", {
        modalData: {
          modalTitle: "",
          text: "Вы не можете удалить категорию, в которой находятся задачи",
        },
      });
    }
  };

  if (hideEmptyOptions && group.title !== "Прочие задачи") return <></>;

  return (
    <>
      {!categoryId ||
      categoryId === 0 ||
      (categoryId && categoryId === group.id) ? (
        <>
          <TableRow
            className={clsx(styles.row, styles.cell_section)}
            onClick={onToggleOpen}
          >
            <TableCell align="left" component="th" scope="row">
              <div className={styles.section_row}>
                {withCheckbox && (
                  <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    color="primary"
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    checked={isSelected}
                    onChange={(e) =>
                      onSelectChange(group.id ?? -1, e.target.checked)
                    }
                  />
                )}
                <p
                  className={clsx(styles.arrow_drop, {
                    [styles.arrow_drop__opened]: isOpen,
                  })}
                >
                  <ArrowDropDownSVG />
                </p>
                <span className={styles.section_row_title}>
                  {group.title}
                  <Badge
                    badgeContent={group.count}
                    color="primary"
                    sx={{
                      marginLeft: "25px",
                      opacity: group.count && +group.count === 0 ? ".5" : "1",
                    }}
                  />
                </span>
              </div>
            </TableCell>
            {!isCompanyDefined && <TableCell align="left"></TableCell>}
            <TableCell align="left"></TableCell>
            <TableCell align="left"></TableCell>
            <TableCell align="left"></TableCell>
            <TableCell align="left"></TableCell>
            <TableCell align="left">
              <div className={styles.cell}>
                {permissions.hasPermission(
                  ACTIONS.companySpecialistWorkPlan_tasks_allowedToCreate
                ) && (
                  <ColorfulButton
                    text={""}
                    onClick={(e) => {
                      e.stopPropagation();
                      open("CreatePlanEventModal", {
                        btnText: "Сохранить",
                        modalData: {
                          modalTitle: "Создать задачу",
                          groupId: group.id,
                          companyId: company?.id,
                          groupTitle: group.title,
                          isCompanyDefined: !isCompanyDefined,
                        },
                      });
                    }}
                  />
                )}
                {isCompanyDefined && (
                  <div className={styles.createTaskBtn}>
                    {permissions.hasPermission(
                      ACTIONS.companySpecialistWorkPlan_groups_allowedToDelete
                    ) && (
                      <WrapperSVG color="">
                        <Tooltip title="Удалить категорию" placement="top">
                          <TrashIcon size={24} onClick={handleGroupDeleting} />
                        </Tooltip>
                      </WrapperSVG>
                    )}
                  </div>
                )}
              </div>
            </TableCell>
          </TableRow>
          {isLoading && (
            <div style={{ margin: "12px" }}>
              <Loader size="lg" color="green" />
            </div>
          )}
          {isOpen && data && (
            <>
              <TableRow>
                {!isCompanyDefined && (
                  <TableCell
                    id="column_1"
                    className={clsx(styles.cell_border, "resizable")}
                    align="left"
                    width={columnsWidth[0]}
                  >
                    <div
                      className={clsx(
                        styles.cell_header,
                        styles.cellHeadCompeny
                      )}
                    >
                      <span>Компания</span>
                    </div>
                    <div
                      id="column_1_resizer"
                      className={clsx(styles.resizer, "resizer")}
                    ></div>
                  </TableCell>
                )}
                <TableCell
                  id="column_2"
                  className={clsx(styles.cell_border, "resizable")}
                  align="left"
                  width={columnsWidth[1]}
                >
                  <div className={styles.cell_header}>
                    {withCheckbox && (
                      <Checkbox
                        color="primary"
                        icon={<CheckboxSVG />}
                        checkedIcon={<CheckboxCheckedSVG />}
                        checked={choice.areAllItemsSelected}
                        onChange={(e) => choice.selectAll(e.target.checked)}
                      />
                    )}
                    <span>Задача</span>
                  </div>
                  <div
                    id="column_2_resizer"
                    className={clsx(styles.resizer, "resizer")}
                  ></div>
                </TableCell>
                <TableCell
                  id="column_3"
                  className={clsx(styles.cell_border, "resizable")}
                  align="left"
                  width={columnsWidth[2]}
                >
                  <div className={styles.cell_header}>
                    <span>Дата создания</span>
                    <div
                      className={clsx(styles.sort_icon, {
                        [styles.sort_icon_active]:
                          sorting.shouldIconRotate("startDateOrder"),
                      })}
                      onClick={() =>
                        sorting.onItemSortingChange("startDateOrder")
                      }
                    >
                      <GreyArrowDownSVG />
                    </div>
                  </div>
                  <div
                    id="column_3_resizer"
                    className={clsx(styles.resizer, "resizer")}
                  ></div>
                </TableCell>
                <TableCell
                  id="column_4"
                  className={clsx(styles.cell_border, "resizable")}
                  width={columnsWidth[3]}
                  align="left"
                >
                  <div className={styles.cell_header}>
                    <span>Крайний срок</span>
                    <div
                      className={clsx(styles.sort_icon, {
                        [styles.sort_icon_active]:
                          sorting.shouldIconRotate("deadlineOrder"),
                      })}
                      onClick={() =>
                        sorting.onItemSortingChange("deadlineOrder")
                      }
                    >
                      <GreyArrowDownSVG />
                    </div>
                  </div>
                  <div
                    id="column_4_resizer"
                    className={clsx(styles.resizer, "resizer")}
                  ></div>
                </TableCell>
                <TableCell
                  id="column_5"
                  className={clsx(styles.cell_border, "resizable")}
                  width={columnsWidth[4]}
                  align="left"
                >
                  <div className={styles.cell_header}>
                    <span>Статус</span>
                    <div
                      className={clsx(styles.sort_icon, {
                        [styles.sort_icon_active]:
                          sorting.shouldIconRotate("statusOrder"),
                      })}
                      onClick={() => sorting.onItemSortingChange("statusOrder")}
                    >
                      <GreyArrowDownSVG />
                    </div>
                  </div>
                  <div
                    id="column_5_resizer"
                    className={clsx(styles.resizer, "resizer")}
                  ></div>
                </TableCell>
                <TableCell
                  id="column_6"
                  className={clsx(styles.cell_border, "resizable")}
                  width={columnsWidth[5]}
                  align="left"
                >
                  <div className={styles.cell_header}>
                    <span>Примечание</span>
                  </div>
                  <div
                    id="column_6_resizer"
                    className={clsx(styles.resizer, "resizer")}
                  ></div>
                </TableCell>
                <TableCell
                  id="column_7"
                  width={columnsWidth[6]}
                  className={clsx(styles.cell_border, "resizable")}
                  align="right"
                >
                  {withCheckbox &&
                    choice.selectedItems.length > 0 &&
                    permissions.hasPermission(
                      ACTIONS.companySpecialistWorkPlan_groups_allowedToDelete
                    ) && (
                      <div className={styles.selectedGroupsActions}>
                        <WrapperSVG color="">
                          <Tooltip title="Удалить категорию" placement="top">
                            <TrashIcon size={24} onClick={onDeleteSelectedGroups} />
                          </Tooltip>
                        </WrapperSVG>
                      </div>
                    )}
                </TableCell>
              </TableRow>
              {data.pages.map((page) =>
                page.tasks.map((task) => (
                  <WorkplanGroupItem
                    key={task.id}
                    task={task}
                    group={group}
                    isCompanyDefined={isCompanyDefined}
                  />
                ))
              )}
              {hasNextPage && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Button
                      size="small"
                      onClick={() => {
                        fetchNextPage();
                      }}
                    >
                      {isFetchingNextPage ? (
                        <Loader size="sm" color="white" />
                      ) : (
                        "Загрузить еще"
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </>
          )}
        </>
      ) : null}
    </>
  );
};
